(function () {
  if (window.__ksListingRestore) return;
  window.__ksListingRestore = true;

  const STORAGE_KEY = 'ks:listing-restore';
  const SCROLL_CACHE_KEY = 'ks:listing-scroll';
  const PAGE_NAV_KEY = 'ks:listing-page-nav';
  const MAX_AJAXINATE_PAGES = 20;
  const STATE_MAX_AGE = 30 * 60 * 1000;
  const GRACE_MS = 4000;
  const RELOAD_GUARD_MS = 3000;
  const HIGHLIGHT_CLASS = 'is-listing-restore-target';
  const QUICK_ADD_SELECTOR = '.quick-add__holder, [data-quick-add-btn], [data-add-to-cart]';
  const CARD_SELECTOR = '[data-listing-card], [data-grid-item], .ksc-card[data-url]';
  const LINK_SELECTOR = 'a[data-product-link], a.product-link';
  const LISTING_TEMPLATE_CLASS = /template-(collection|search)/;
  const VOLATILE_PARAMS = new Set([
    'variant',
    'fbclid',
    'gclid',
    'gbraid',
    'wbraid',
    '_ab',
    '_fd',
    '_sc',
    'pb',
    'preview_theme_id',
    'ks_restore_debug',
  ]);
  const VOLATILE_PREFIXES = ['utm_', 'mc_'];

  const PENDING_CLASS = 'ks-listing-restore-pending';
  const REVEALING_CLASS = 'ks-listing-restore-revealing';

  let scrollCacheTimer = null;
  let scrollCacheListenerAttached = false;
  let scrollRestorationListenerAttached = false;
  let restoreStarted = false;
  let revealTimer = null;
  let lastShownAt = Date.now();
  let reloadGuardAttached = false;
  let originalLocationReload = null;
  let safariReloadGuardTimer = null;
  const DEBUG_KEY = 'ks:restore-debug-log';
  const DEBUG_MAX = 60;
  let debugCache = null;

  function isDebugEnabled() {
    try {
      if (window.location.search.includes('ks_restore_debug=1')) {
        window.sessionStorage.setItem('ks:restore-debug-on', '1');
        return true;
      }
      if (window.sessionStorage.getItem('ks:restore-debug-on') === '1') return true;
      return window.localStorage.getItem('ks:restore-debug') === '1';
    } catch (error) {
      return false;
    }
  }

  // The failing app-triggered reload discards in-memory state, so the debug log
  // is persisted in sessionStorage. This lets the overlay show events from
  // BEFORE the reload that wiped the page, which is exactly the failing cycle.
  function readDebugLines() {
    if (debugCache) return debugCache;
    try {
      const raw = window.sessionStorage.getItem(DEBUG_KEY);
      debugCache = raw ? JSON.parse(raw) : [];
    } catch (error) {
      debugCache = [];
    }
    if (!Array.isArray(debugCache)) debugCache = [];
    return debugCache;
  }

  function debugLog(entry) {
    if (!isDebugEnabled()) return;
    const line = { ...entry, t: Date.now() % 1000000 };
    const lines = readDebugLines();
    lines.push(line);
    while (lines.length > DEBUG_MAX) lines.shift();
    try {
      window.sessionStorage.setItem(DEBUG_KEY, JSON.stringify(lines));
    } catch (error) {}
    console.log('[ks-listing-restore]', line);
    updateDebugOverlay();
  }

  function updateDebugOverlay() {
    if (!document.body || !isDebugEnabled()) return;
    let el = document.getElementById('ks-restore-debug');
    if (!el) {
      el = document.createElement('div');
      el.id = 'ks-restore-debug';
      el.setAttribute('aria-hidden', 'true');
      el.style.cssText =
        'position:fixed;bottom:0;left:0;right:0;max-height:45vh;overflow:auto;background:rgba(0,0,0,0.88);color:#9f9;font:10px/1.3 monospace;padding:8px 8px 24px;z-index:2147483647;pointer-events:none;white-space:pre-wrap;';
      document.body.appendChild(el);
    }
    el.textContent = readDebugLines()
      .slice(-18)
      .map((line) => JSON.stringify(line))
      .join('\n');
  }

  function revealPage() {
    if (revealTimer) {
      window.clearTimeout(revealTimer);
      revealTimer = null;
    }

    const html = document.documentElement;
    if (!html.classList.contains(PENDING_CLASS)) {
      html.classList.remove(REVEALING_CLASS);
      return;
    }

    html.classList.add(REVEALING_CLASS);
    window.setTimeout(() => {
      html.classList.remove(PENDING_CLASS);
      html.classList.remove(REVEALING_CLASS);
    }, 180);
  }

  function waitForViewportImages(maxWait) {
    if (!document.documentElement.classList.contains(PENDING_CLASS)) {
      return Promise.resolve();
    }

    const images = Array.from(document.querySelectorAll('img')).filter((img) => {
      const rect = img.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight;
    });
    const pending = images.filter((img) => !img.complete);

    if (!pending.length) return Promise.resolve();

    return new Promise((resolve) => {
      let settled = 0;
      const need = pending.length;
      const done = () => {
        settled += 1;
        if (settled >= need) resolve();
      };

      pending.forEach((img) => {
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      });

      window.setTimeout(resolve, maxWait);
    });
  }

  async function revealWhenReady(maxWait) {
    await waitForViewportImages(maxWait);
    await new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
    revealPage();
  }

  function isVolatileParam(key) {
    if (VOLATILE_PARAMS.has(key)) return true;
    return VOLATILE_PREFIXES.some((prefix) => key.startsWith(prefix));
  }

  function normalizeListingUrl(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      const significant = new URLSearchParams();

      parsed.searchParams.forEach((value, key) => {
        if (isVolatileParam(key)) return;
        significant.append(key, value);
      });

      const entries = Array.from(significant.entries()).sort((a, b) => {
        if (a[0] === b[0]) return a[1].localeCompare(b[1]);
        return a[0].localeCompare(b[0]);
      });

      const qs = new URLSearchParams(entries).toString();
      return parsed.pathname + (qs ? `?${qs}` : '');
    } catch (error) {
      return window.location.pathname + window.location.search;
    }
  }

  function listingUrlsMatch(savedUrl, currentUrl) {
    if (!savedUrl || !currentUrl) return false;
    return normalizeListingUrl(savedUrl) === normalizeListingUrl(currentUrl);
  }

  function listingPathname(url) {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (error) {
      return window.location.pathname;
    }
  }

  // Same collection/search path, ignoring the ?page (and other) params. Used to
  // detect the confirmed Safari failure: on a repeat Back, the browser reloads
  // the collection at page 1 (dropping ?page=2), so an exact URL match fails
  // even though we have valid saved state for page 2.
  function listingPathsMatch(savedUrl, currentUrl) {
    if (!savedUrl || !currentUrl) return false;
    return listingPathname(savedUrl) === listingPathname(currentUrl);
  }

  function urlPageParam(url) {
    try {
      return new URL(url, window.location.origin).searchParams.get('page') || '';
    } catch (error) {
      return '';
    }
  }

  function extractProductHandle(href) {
    if (!href) return null;
    try {
      const match = new URL(href, window.location.origin).pathname.match(/\/products\/([^/]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (error) {
      return null;
    }
  }

  function isListingPage() {
    return LISTING_TEMPLATE_CLASS.test(document.body.className);
  }

  function applyScrollRestorationPolicy() {
    if (!('scrollRestoration' in window.history)) return;
    window.history.scrollRestoration = isListingPage() ? 'manual' : 'auto';
  }

  function getRestoreState() {
    return window.__ksListingRestoreState || null;
  }

  function readStoredState(storageKey) {
    try {
      const raw = window.sessionStorage.getItem(storageKey);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed) return null;
      if (parsed.savedAt && Date.now() - parsed.savedAt > STATE_MAX_AGE) return null;
      if (parsed.consumedAt && Date.now() - parsed.consumedAt > GRACE_MS) return null;
      if (!listingUrlsMatch(parsed.listingUrl, window.location.href)) return null;

      return parsed;
    } catch (error) {
      return null;
    }
  }

  // Reads valid saved state whose collection path matches the current path,
  // even if the ?page param differs. Returns null when the exact matcher would
  // already have accepted it (so callers only use this for the page-mismatch
  // recovery path).
  function readStoredStateLoose(storageKey) {
    try {
      const raw = window.sessionStorage.getItem(storageKey);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed) return null;
      if (parsed.savedAt && Date.now() - parsed.savedAt > STATE_MAX_AGE) return null;
      if (parsed.consumedAt && Date.now() - parsed.consumedAt > GRACE_MS) return null;
      if (!listingPathsMatch(parsed.listingUrl, window.location.href)) return null;
      if (listingUrlsMatch(parsed.listingUrl, window.location.href)) return null;

      return parsed;
    } catch (error) {
      return null;
    }
  }

  function markStateConsumed(storageKey) {
    try {
      const raw = window.sessionStorage.getItem(storageKey);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      if (!parsed || parsed.consumedAt) return;

      parsed.consumedAt = Date.now();
      window.sessionStorage.setItem(storageKey, JSON.stringify(parsed));
    } catch (error) {}
  }

  // Only the PDP-return marker (STORAGE_KEY) is cleared after use. The scroll
  // cache is intentionally kept: it is continuously overwritten by the scroll
  // listener and TTLs out on its own, so every return (1st, 2nd, Nth) has an
  // identical scroll fallback for the current URL even if an app reload wipes
  // the page. Clearing it was what made repeated returns behave differently.
  function clearStateAfterGrace() {
    window.setTimeout(() => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          window.sessionStorage.removeItem(STORAGE_KEY);
        });
      });
    }, GRACE_MS);
  }

  function writeState(state) {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...state,
        savedAt: Date.now(),
      })
    );
  }

  function writeScrollCache(scrollY) {
    if (!isListingPage() || typeof scrollY !== 'number') return;

    window.sessionStorage.setItem(
      SCROLL_CACHE_KEY,
      JSON.stringify({
        listingUrl: normalizeListingUrl(window.location.href),
        scrollY: Math.max(0, Math.round(scrollY)),
        savedAt: Date.now(),
      })
    );
  }

  function isModifiedClick(event) {
    const link = event.target.closest('a[href]');
    if (link?.target === '_blank' || link?.hasAttribute('download')) return true;
    return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
  }

  function isQuickAddClick(target) {
    return Boolean(target.closest(QUICK_ADD_SELECTOR));
  }

  function getListingCard(target) {
    return target.closest(CARD_SELECTOR);
  }

  function getProductHref(target, card) {
    const link = target.closest(LINK_SELECTOR);
    if (link?.href) return link.href;
    if (card?.dataset?.url) return card.dataset.url;
    const nestedLink = card?.querySelector(LINK_SELECTOR);
    return nestedLink?.href || null;
  }

  function getTabId(card) {
    const tabContent = card?.closest('.tab-content');
    return tabContent?.id || null;
  }

  function isTabCurrent(tabId) {
    if (!tabId) return true;
    const tabContent = document.getElementById(tabId);
    return Boolean(tabContent?.classList.contains('current'));
  }

  function getHorizontalContext(card) {
    const track = card?.closest('[id^="ksc-track-"]');
    if (!track?.id) return null;
    return {
      trackId: track.id,
      scrollLeft: track.scrollLeft,
    };
  }

  function getAjaxinateSectionId(card) {
    const loop = card?.closest('[id="AjaxinateLoop"]') || document.getElementById('AjaxinateLoop');
    return loop?.dataset?.ajaxinateId || null;
  }

  function saveListingPosition(card, href) {
    const handle = card?.dataset?.productHandle || extractProductHandle(href);
    if (!handle) return;

    writeState({
      listingUrl: normalizeListingUrl(window.location.href),
      cardId: card.id || null,
      productHandle: handle,
      scrollY: window.scrollY,
      ajaxinateSectionId: getAjaxinateSectionId(card),
      tabId: getTabId(card),
      horizontal: getHorizontalContext(card),
    });

    debugLog({ event: 'save_position', handle, scrollY: window.scrollY, url: normalizeListingUrl(window.location.href) });
  }

  function handleSaveClick(event) {
    if (isModifiedClick(event) || isQuickAddClick(event.target)) return;

    const card = getListingCard(event.target);
    const href = getProductHref(event.target, card);
    if (!href || !extractProductHandle(href)) return;

    if (!card && !event.target.closest(LINK_SELECTOR)) return;

    saveListingPosition(card || event.target.closest('[data-grid-item]'), href);
  }

  function activateTab(tabId) {
    if (!tabId || isTabCurrent(tabId)) return Promise.resolve();

    const tabContent = document.getElementById(tabId);
    if (!tabContent) return Promise.resolve();

    const tabClass = Array.from(tabContent.classList).find((className) => className.startsWith('tab-content-'));
    if (!tabClass) return Promise.resolve();

    const tabIndex = tabClass.replace('tab-content-', '');
    const tabsComponent = tabContent.closest('tabs-component');
    const tabLink = tabsComponent?.querySelector(`[data-tab="${tabIndex}"]`);
    if (!tabLink || tabLink.classList.contains('current')) return Promise.resolve();

    tabLink.click();
    return new Promise((resolve) => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
    });
  }

  function restoreHorizontalScroll(horizontal) {
    if (!horizontal?.trackId) return;
    const track = document.getElementById(horizontal.trackId);
    if (!track) return;
    track.scrollLeft = horizontal.scrollLeft;
  }

  function findCard(state) {
    if (!state?.productHandle && !state?.cardId) return null;

    if (state.cardId) {
      const byId = document.getElementById(state.cardId);
      if (byId) return byId;
    }

    if (state.productHandle) {
      const matches = document.querySelectorAll(`[data-product-handle="${state.productHandle}"]`);
      if (matches.length === 1) return matches[0];
      if (state.cardId) {
        const scoped = Array.from(matches).find((card) => card.id === state.cardId);
        if (scoped) return scoped;
      }
      return matches[0] || null;
    }

    return null;
  }

  async function loadNextAjaxinatePage(sectionId) {
    const pagination = document.querySelector(`#AjaxinatePagination[data-ajaxinate-id="${sectionId}"]`);
    const nextLink = pagination?.querySelector('a[href]');
    if (!nextLink?.href) return false;

    const response = await fetch(nextLink.href);
    if (!response.ok) return false;

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const containerSelector = `#AjaxinateLoop[data-ajaxinate-id="${sectionId}"]`;
    const newItems = doc.querySelector(containerSelector);
    const newPagination = doc.querySelector(`#AjaxinatePagination[data-ajaxinate-id="${sectionId}"]`);
    const container = document.querySelector(containerSelector);

    if (!container || !newItems) return false;

    container.insertAdjacentHTML('beforeend', newItems.innerHTML);

    if (pagination) {
      pagination.innerHTML = newPagination ? newPagination.innerHTML : '';
    }

    return true;
  }

  async function ensureCardInDom(state) {
    let card = findCard(state);
    if (card || !state.ajaxinateSectionId) return card;

    for (let page = 0; page < MAX_AJAXINATE_PAGES; page += 1) {
      card = findCard(state);
      if (card) return card;

      const loaded = await loadNextAjaxinatePage(state.ajaxinateSectionId);
      if (!loaded) break;
    }

    return findCard(state);
  }

  function scrollToY(scrollY) {
    if (typeof scrollY !== 'number') return;

    window.scrollTo({
      top: Math.max(0, scrollY),
      left: 0,
      behavior: 'instant',
    });
  }

  function highlightCard(card) {
    card.classList.add(HIGHLIGHT_CLASS);
    window.setTimeout(() => {
      card.classList.remove(HIGHLIGHT_CLASS);
    }, 1800);
  }

  function cleanupRestoreState() {
    delete window.__ksListingRestoreState;
    delete window.__ksListingRestoreFromPdp;
    delete window.__ksListingRestoreScrollOnly;
  }

  function resolveRestoreContext() {
    let state = getRestoreState();
    let fromPdp = Boolean(window.__ksListingRestoreFromPdp);
    let scrollOnly = Boolean(window.__ksListingRestoreScrollOnly);
    let source = 'window';

    if (!state) {
      const restoreState = readStoredState(STORAGE_KEY);
      if (restoreState) {
        state = restoreState;
        fromPdp = true;
        scrollOnly = false;
        source = STORAGE_KEY;
      } else {
        const scrollState = readStoredState(SCROLL_CACHE_KEY);
        if (scrollState) {
          state = scrollState;
          fromPdp = false;
          scrollOnly = true;
          source = SCROLL_CACHE_KEY;
        }
      }
    }

    return { state, fromPdp, scrollOnly, source };
  }

  function setAppSkipReloadFlags() {
    if (!isListingPage()) return;

    try {
      window.lbUpsellComponents = window.lbUpsellComponents || {};
      window.lbUpsellComponents.isBFRefreshIgnored = true;
      window.mvIgnoreBFRefresh = true;
      window.multivariantsIgnoreBFRefresh = true;
    } catch (error) {}
  }

  function restoreOriginalReload() {
    if (!originalLocationReload) return;
    try {
      try {
        delete window.location.reload;
      } catch (error) {}
      if (window.location.reload !== originalLocationReload) {
        window.location.reload = originalLocationReload;
      }
    } catch (error) {}
  }

  // window.location.reload is non-writable in Safari, so a plain assignment
  // silently fails. We try Object.defineProperty and fall back to assignment,
  // wrapping everything so this can never throw and abort the restore flow.
  function enableSafariReloadGuard() {
    try {
      if (!isListingPage()) return;
      if (Date.now() - lastShownAt > RELOAD_GUARD_MS) return;

      setAppSkipReloadFlags();

      if (!originalLocationReload) {
        originalLocationReload = window.location.reload.bind(window.location);
      }

      const guardedReload = function guardedReload(forcedReload) {
        if (isListingPage() && Date.now() - lastShownAt < RELOAD_GUARD_MS) {
          debugLog({ event: 'reload_blocked', forcedReload: Boolean(forcedReload) });
          return undefined;
        }
        return originalLocationReload(forcedReload);
      };

      let installed = false;
      try {
        Object.defineProperty(window.location, 'reload', {
          configurable: true,
          writable: true,
          value: guardedReload,
        });
        installed = window.location.reload === guardedReload;
      } catch (error) {}

      if (!installed) {
        try {
          window.location.reload = guardedReload;
          installed = window.location.reload === guardedReload;
        } catch (error) {}
      }

      debugLog({ event: 'reload_guard', installed });

      if (safariReloadGuardTimer) {
        window.clearTimeout(safariReloadGuardTimer);
      }

      safariReloadGuardTimer = window.setTimeout(restoreOriginalReload, RELOAD_GUARD_MS);
    } catch (error) {
      debugLog({ event: 'reload_guard_error', message: String(error && error.message) });
    }
  }

  function attachScrollRestorationPolicy() {
    if (scrollRestorationListenerAttached) return;
    scrollRestorationListenerAttached = true;

    applyScrollRestorationPolicy();

    window.addEventListener('pagehide', () => {
      if (isListingPage()) {
        writeScrollCache(window.scrollY);
        setAppSkipReloadFlags();
        return;
      }

      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    });
  }

  function attachReloadGuard() {
    if (reloadGuardAttached) return;
    reloadGuardAttached = true;

    if (!window.navigation || typeof window.navigation.addEventListener !== 'function') return;

    window.navigation.addEventListener('navigate', (event) => {
      try {
        if (event.navigationType !== 'reload') return;
        if (event.cancelable === false) return;
        if (!isListingPage()) return;
        if (Date.now() - lastShownAt > RELOAD_GUARD_MS) return;
        event.preventDefault();
        debugLog({ event: 'navigation_reload_blocked' });
      } catch (error) {}
    });
  }

  function finishRestore(reason) {
    window.__ksListingRestoreHandled = true;
    revealPage();
    cleanupRestoreState();
    initScrollCacheListener();
    debugLog({ event: 'finish_restore', reason, scrollY: window.scrollY });
  }

  // Confirmed Safari failure: on a repeat Back, the collection reloads at page 1
  // (the ?page=2 param is dropped and it is a fresh load, not bfcache), so exact
  // URL matching fails and the shopper is dumped at the top of page 1. When we
  // still hold valid PDP-return state for a different page of the SAME
  // collection, navigate once to that saved URL so the shopper lands back on
  // their page, where the normal exact-match restore then runs. Guarded by a
  // per-URL sessionStorage flag so it can never loop.
  function recoverPageMismatch() {
    // Only recover from the PDP-return marker (the shopper actually opened a
    // product from this listing). The scroll cache is deliberately excluded to
    // avoid redirecting unrelated visits.
    const looseState = readStoredStateLoose(STORAGE_KEY);
    if (!looseState || !looseState.listingUrl) return false;

    const savedNormalized = normalizeListingUrl(looseState.listingUrl);
    if (urlPageParam(looseState.listingUrl) === urlPageParam(window.location.href)) return false;
    if (savedNormalized === normalizeListingUrl(window.location.href)) return false;

    let alreadyTried = '';
    try {
      alreadyTried = window.sessionStorage.getItem(PAGE_NAV_KEY) || '';
    } catch (error) {}

    if (alreadyTried === savedNormalized) {
      // We already redirected here once and still mismatch; stop to avoid loops.
      try {
        window.sessionStorage.removeItem(PAGE_NAV_KEY);
      } catch (error) {}
      debugLog({ event: 'page_nav_giveup', savedUrl: savedNormalized });
      return false;
    }

    try {
      window.sessionStorage.setItem(PAGE_NAV_KEY, savedNormalized);
    } catch (error) {}

    debugLog({
      event: 'page_nav_recover',
      from: normalizeListingUrl(window.location.href),
      to: savedNormalized,
    });

    // Keep the cover up through the navigation so the page-1 flash is hidden.
    document.documentElement.classList.add(PENDING_CLASS);
    window.location.replace(looseState.listingUrl);
    return true;
  }

  async function restore(options) {
    const reason = options?.reason || 'unknown';
    const persisted = Boolean(options?.persisted);

    if (restoreStarted) return;
    if (window.__ksListingRestoreHandled && !persisted) return;

    const { state, fromPdp, scrollOnly, source } = resolveRestoreContext();

    debugLog({
      event: 'restore_start',
      reason,
      persisted,
      source,
      currentUrl: normalizeListingUrl(window.location.href),
      savedUrl: state?.listingUrl ? normalizeListingUrl(state.listingUrl) : null,
      urlMatch: state ? listingUrlsMatch(state.listingUrl, window.location.href) : false,
    });

    if (!state || !listingUrlsMatch(state.listingUrl, window.location.href)) {
      if (recoverPageMismatch()) return;
      finishRestore('no_state');
      return;
    }

    restoreStarted = true;
    window.__ksListingRestoreHandled = true;

    // Landed on a matching URL: clear the one-shot page-nav recovery guard.
    try {
      window.sessionStorage.removeItem(PAGE_NAV_KEY);
    } catch (error) {}

    // Only mark the PDP-return marker consumed. The scroll cache stays an
    // always-available fallback for this URL (overwritten as the user scrolls).
    markStateConsumed(STORAGE_KEY);
    setAppSkipReloadFlags();
    enableSafariReloadGuard();

    const targetY = typeof state.scrollY === 'number' ? state.scrollY : null;

    try {
      if (fromPdp) {
        await activateTab(state.tabId);
        restoreHorizontalScroll(state.horizontal);

        if (!findCard(state) && state.ajaxinateSectionId) {
          await ensureCardInDom(state);
        }

        const card = findCard(state);

        if (typeof targetY === 'number') {
          scrollToY(targetY);
        }

        await revealWhenReady(1500);

        if (card) {
          highlightCard(card);
        }
      } else if (scrollOnly && typeof targetY === 'number') {
        scrollToY(targetY);
        await revealWhenReady(1500);
      } else if (persisted) {
        const card = findCard(state);
        if (typeof targetY === 'number' && Math.abs(window.scrollY - targetY) > 24) {
          scrollToY(targetY);
        }
        if (card) {
          highlightCard(card);
        }
        writeScrollCache(window.scrollY);
        revealPage();
      } else {
        revealPage();
      }
    } finally {
      revealPage();
      cleanupRestoreState();
      clearStateAfterGrace();
      initScrollCacheListener();
      debugLog({ event: 'restore_done', reason, scrollY: window.scrollY });
    }
  }

  function initScrollCacheListener() {
    if (!isListingPage() || scrollCacheListenerAttached) return;
    scrollCacheListenerAttached = true;

    const saveDebounced = () => {
      window.clearTimeout(scrollCacheTimer);
      scrollCacheTimer = window.setTimeout(() => {
        writeScrollCache(window.scrollY);
      }, 100);
    };

    window.addEventListener('scroll', saveDebounced, { passive: true });
  }

  attachScrollRestorationPolicy();
  attachReloadGuard();

  if (document.documentElement.classList.contains(PENDING_CLASS)) {
    revealTimer = window.setTimeout(revealPage, 4000);
    window.addEventListener('load', revealPage, { once: true });
  }

  document.addEventListener('click', handleSaveClick, true);

  window.addEventListener(
    'pageshow',
    (event) => {
      lastShownAt = Date.now();
      applyScrollRestorationPolicy();
      setAppSkipReloadFlags();
      enableSafariReloadGuard();

      debugLog({
        event: 'pageshow',
        persisted: event.persisted,
        scrollY: window.scrollY,
        url: normalizeListingUrl(window.location.href),
      });

      if (!event.persisted) return;

      restoreStarted = false;
      delete window.__ksListingRestoreHandled;
      restore({ reason: 'pageshow_bfcache', persisted: true });
    },
    true
  );

  function bootRestore() {
    applyScrollRestorationPolicy();
    if (isDebugEnabled() && document.body) {
      updateDebugOverlay();
    }
    restore({ reason: 'boot', persisted: false });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootRestore, { once: true });
  } else {
    bootRestore();
  }
})();
